import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Stack
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from '../Snackbar';
import { verifyOtp, resendOtp } from '../../api/userApi';
import { useAuth } from '../../context/AuthContext';

export default function OtpVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { isAuthenticated } = useAuth();
  const email = atob(params.get('i'));

  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);


  useEffect(() => {
    if (isAuthenticated) {
      navigate('/patient', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const focusInput = (index) => {
    if (index >= 0 && index < 6) {
      inputRefs.current[index]?.focus();
    }
  };

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otpValues];
    newOtp[index] = value.slice(-1);
    setOtpValues(newOtp);

    if (value && index < 5) focusInput(index + 1);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (otpValues[index]) {
        const newOtp = [...otpValues];
        newOtp[index] = '';
        setOtpValues(newOtp);
      } else if (index > 0) {
        focusInput(index - 1);
      }
    }
  };

  const getOtp = () => otpValues.join('');

  const handleVerify = async () => {
    const otp = getOtp();
    if (otp.length < 6) {
      showSnackbar('Please enter a valid 6-digit OTP.', 'error');
      return;
    }

    try {
      const res = await verifyOtp({ email, otp });
      if (res.data.succeeded) {
        showSnackbar(res.data.messages[0], 'success');
        navigate('/');
      } else {
        showSnackbar(res.data.messages[0], 'error');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Invalid OTP', 'error');
    }
  };

  const handleResend = async () => {
    try {
      const res = await resendOtp(email);
      if (res.data.succeeded) {
        setOtpValues(['', '', '', '', '', '']);
        setResendCooldown(30);
        showSnackbar(res.data.messages[0], 'success');
      } else {
        showSnackbar(res.data.messages[0], 'error');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to resend OTP', 'error');
    }
  };

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8 }}>
      <Typography variant="h6" gutterBottom>
        OTP Verification
      </Typography>
      <Typography variant="body2" gutterBottom>
        Enter the 6-digit OTP sent to <strong>{email}</strong>
      </Typography>

      <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
        {otpValues.map((value, index) => (
          <TextField
            key={index}
            inputRef={(el) => (inputRefs.current[index] = el)}
            value={value}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            slotProps={{
              input: {
                maxLength: 1
              },
              htmlInput: {
                style: { textAlign: 'center', fontSize: 20, width: 40 },
              }
            }}
          />
        ))}
      </Stack>

      <Button
        fullWidth
        variant="contained"
        sx={{ mt: 3 }}
        onClick={handleVerify}
      >
        Verify
      </Button>

      <Button
        fullWidth
        sx={{ mt: 2 }}
        disabled={resendCooldown > 0}
        onClick={handleResend}
      >
        {resendCooldown > 0
          ? `Resend OTP in ${resendCooldown}s`
          : 'Resend OTP'}
      </Button>
    </Box>
  );
}
